import type { PaigeClient, components } from '@paige/api-client';
import { unwrap } from './http';

type S = components['schemas'];
export type TenantCredits = S['TenantCredits'];
export type PendingInvite = S['PendingInvite'];
export type StaffPerson = S['StaffPerson'];
export type DeliveryRetryResult = S['DeliveryRetryResult'];
export type ExportRequeueResult = S['ExportRequeueResult'];
export type JobLimit = S['JobLimit'];

/** A dry run counts and writes nothing; the run itself carries the reason the company's activity shows. */
/** Whose two-step sign-in support resets, why, and the key that makes it once. */
export interface SecondFactorReset {
  userId: string;
  reason: string;
  key: string;
}

export type ExportFix = { dryRun: true } | { dryRun: false; reason: string };

/** A person's two-step sign-in reset after a lost phone, and the people to pick from (AdminSecondFactorEndpoints). */
function twoStepApi(client: PaigeClient) {
  return {
    async listPeople(tenantId: string): Promise<StaffPerson[]> {
      const r = await client.GET('/admin/tenants/{tenantId}/people', {
        params: { path: { tenantId } },
      });
      return unwrap(r);
    },
    /** `key` is the dialog's own, so a second press or a retry is the same reset, made once. */
    async resetSecondFactor(tenantId: string, reset: SecondFactorReset): Promise<void> {
      const r = await client.POST('/admin/tenants/{tenantId}/people/{userId}/second-factor/reset', {
        params: {
          path: { tenantId, userId: reset.userId },
          header: { 'Idempotency-Key': reset.key },
        },
        body: { reason: reset.reason },
      });
      unwrap(r);
    },
  };
}

/**
 * Support's fixes from the customer page, round 3 (docs/engineering/admin-customer-insights.md §0):
 * the goodwill left, the pending invites and their resend, a destination's failed deliveries sent
 * again, and accepted documents that never exported requeued (AdminExportFixEndpoints); and a person's
 * two-step sign-in reset after a lost phone (AdminSecondFactorEndpoints).
 */
export function supportFixesApi(client: PaigeClient) {
  return { ...exportFixesApi(client), ...twoStepApi(client), ...jobLimitApi(client) };
}

/** A company's job limit: 1,000 unless billing staff changed it, with a reason (AdminJobLimitEndpoints, 0299). */
function jobLimitApi(client: PaigeClient) {
  return {
    async getJobLimit(tenantId: string): Promise<JobLimit> {
      const r = await client.GET('/admin/tenants/{tenantId}/job-limit', {
        params: { path: { tenantId } },
      });
      return unwrap(r);
    },
    async setJobLimit(
      tenantId: string,
      change: { limit: number; reason: string },
    ): Promise<JobLimit> {
      const r = await client.PUT('/admin/tenants/{tenantId}/job-limit', {
        params: { path: { tenantId } },
        body: change,
      });
      return unwrap(r);
    },
  };
}

/** Credits, invites and re-sent exports. */
function exportFixesApi(client: PaigeClient) {
  return {
    async getCredits(tenantId: string): Promise<TenantCredits> {
      const r = await client.GET('/admin/tenants/{tenantId}/credits', {
        params: { path: { tenantId } },
      });
      return unwrap(r);
    },
    async listInvites(tenantId: string): Promise<PendingInvite[]> {
      const r = await client.GET('/admin/tenants/{tenantId}/invites', {
        params: { path: { tenantId } },
      });
      return unwrap(r);
    },
    async resendInvite(tenantId: string, email: string): Promise<void> {
      const r = await client.POST('/admin/tenants/{tenantId}/invites/{email}/send', {
        params: { path: { tenantId, email } },
      });
      unwrap(r);
    },
    async retryFailed(
      tenantId: string,
      destinationId: string,
      fix: ExportFix,
    ): Promise<DeliveryRetryResult> {
      const r = await client.POST(
        '/admin/tenants/{tenantId}/destinations/{destinationId}/retry-failed',
        { params: { path: { tenantId, destinationId } }, body: fix },
      );
      return unwrap(r);
    },
    async requeueExports(tenantId: string, fix: ExportFix): Promise<ExportRequeueResult> {
      const r = await client.POST('/admin/tenants/{tenantId}/exports/requeue', {
        params: { path: { tenantId } },
        body: fix,
      });
      return unwrap(r);
    },
  };
}

export type SupportFixesApi = ReturnType<typeof supportFixesApi>;
